import { Box } from "@chakra-ui/react";
import { colors, customEvent } from "goi_common";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { ParlorDetailProps } from "@/types/parlor";
import { ParlorMultiSplitContainer } from "@/components/ParlorMultiSplitContainer";
import { useCustomVariation } from "@/hooks/useCustomVariation";
import {
  getRecentCompareVisits,
  isVisitedListSubmitSuppressed,
  loadCompareVisits,
  upsertCompareVisit,
} from "@/utils/parlorCompareVisitStorage";
import {
  isVisitedListTreatment,
  PARLOR_VISITED_LIST_EXPERIMENT_KEY,
} from "@/utils/parlorVisitedListExperiment";
import { parlorToCompareVisit } from "../utils/parlorVisitPayload";
import ParlorVisitedListLeadSheet from "./ParlorVisitedListLeadSheet";

/**
 * multi + B안일 때만 신규 칩으로 체크리스트를 대체.
 * 할당은 2곳+ 일 때만 (Split). 제출 후 하루는 체크리스트로 되돌림.
 */
export default function ParlorVisitedListSlot({
  parlor,
  region,
  children,
}: {
  parlor: ParlorDetailProps;
  region: string;
  children: ReactNode;
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    upsertCompareVisit(parlorToCompareVisit(parlor, region));
    setReady(true);
  }, [parlor.id, parlor.companyname, parlor.images, parlor.expected_charge_sum, region]);

  if (!ready) return <>{children}</>;

  return (
    <ParlorMultiSplitContainer
      fallback={<>{children}</>}
      nonMulti={<>{children}</>}
      multi={
        <VisitedListAssigned parlor={parlor} region={region}>
          {children}
        </VisitedListAssigned>
      }
    />
  );
}

function VisitedListAssigned({
  parlor,
  region,
  children,
}: {
  parlor: ParlorDetailProps;
  region: string;
  children: ReactNode;
}) {
  const variation = useCustomVariation(PARLOR_VISITED_LIST_EXPERIMENT_KEY, "Z");
  const isB = isVisitedListTreatment(variation);
  const [open, setOpen] = useState(false);
  const [suppressed, setSuppressed] = useState(true);
  const [visits, setVisits] = useState(() => getRecentCompareVisits(loadCompareVisits(), 3));

  useEffect(() => {
    const next = upsertCompareVisit(parlorToCompareVisit(parlor, region));
    setVisits(getRecentCompareVisits(next, 3));
    setSuppressed(isVisitedListSubmitSuppressed());
  }, [parlor.id, parlor.companyname, parlor.images, parlor.expected_charge_sum, region]);

  const showChip = isB && !suppressed && visits.length >= 2;

  useEffect(() => {
    if (!showChip) return;
    customEvent({
      newGtm: { path: "parlor_detail", trigger_and_target: "uv_visited_list_chip" },
    });
  }, [showChip]);

  if (!showChip) return <>{children}</>;

  const n = visits.length;

  return (
    <>
      <Box
        as="button"
        type="button"
        display="inline-flex"
        flexDirection="column"
        alignItems="flex-end"
        gap="6px"
        px="14px"
        py="10px"
        ml="auto"
        borderRadius="16px"
        bg={colors.white}
        color={colors.black}
        border="none"
        boxShadow="0px 0px 4px rgba(0, 0, 0, 0.5)"
        cursor="pointer"
        textAlign="right"
        onClick={() => {
          customEvent({
            newGtm: { path: "parlor_detail", trigger_and_target: "visited_list_chip-click" },
          });
          setOpen(true);
        }}
      >
        <Box className="button_14_sb" color={colors.black}>
          찾아보신 장례식장 {n}곳의
        </Box>
        <Box className="button_14_sb" color={colors.black}>
          장례 비용 알아보기 🔍
        </Box>
      </Box>
      <ParlorVisitedListLeadSheet
        isOpen={open}
        onClose={() => {
          setOpen(false);
          setSuppressed(isVisitedListSubmitSuppressed());
        }}
        parlors={visits}
      />
    </>
  );
}
